import { escape, first } from 'lodash';
import { useContext, useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { useUnguardedClientPresentationDesignerContext } from '../ClientPresentationDesigner.context';
import { usePresentationStringQuery } from './graphql/PresentationString.generated';
import {
  PRESENTATION_STRINGS,
  PresentationStringQueryData,
} from './usePresentationStringFormatter.formatters';

export interface usePresentationStringFormatterReturnType {
  formatter: (markdownString: string) => string;
  /** Formatter that substitutes variables but does NOT wrap unreplaced
   *  variables in HTML spans. Use this for contexts where the output is
   *  parsed as markdown (e.g. the Lexical rich-text body editor). */
  plainFormatter: (markdownString: string) => string;
  /** Resolve a single variable name to its display value.
   *  Returns null if the variable is unknown or has no data. */
  resolveVariable: (variableName: string) => string | null;
}

/**
 * Return a callback function that can substitute variables within a markdown string
 */
export function usePresentationStringFormatter(): usePresentationStringFormatterReturnType {
  const householdID = useContext(HouseholdDetailsContext)?.householdId || '';
  const presentationID =
    useUnguardedClientPresentationDesignerContext()?.presentationId || '';
  const hasPresentationID = Boolean(presentationID);
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { data, refetch } = usePresentationStringQuery({
    fetchPolicy: 'cache-and-network',
    variables: { householdID, presentationID, hasPresentationID },
    skip: !householdID,
    onError: (error) => {
      reportError('Caught error while loading data for variables', error);
      showFeedback('Could not fetch variable data.', {
        variant: 'error',
        alertActionButton: {
          label: 'Try again',
          buttonProps: {
            onClick: () => refetch(),
          },
        },
      });
    },
  });

  return useMemo<usePresentationStringFormatterReturnType>(() => {
    const household = first(getNodes(data?.households)) ?? null;
    const presentationCreatedAt =
      first(getNodes(data?.clientPresentationV2s))?.createdAt ?? null;
    const queryData: PresentationStringQueryData = {
      household,
      presentationCreatedAt,
    };

    const formatter = (inputString: string) => {
      return presentationStringFormatter({ inputString, queryData });
    };

    const plainFormatter = (inputString: string) => {
      return presentationStringFormatter({
        inputString,
        queryData,
        highlightUnreplaced: false,
      });
    };

    const resolveVariable = (variableName: string): string | null => {
      const variable =
        PRESENTATION_STRINGS[variableName as keyof typeof PRESENTATION_STRINGS];
      if (!variable) return null;
      return variable.formatter(queryData);
    };

    return { formatter, plainFormatter, resolveVariable };
  }, [data?.households, data?.clientPresentationV2s]);
}

export interface presentationStringFormatterInput {
  inputString: string;
  queryData: PresentationStringQueryData | null;
  /** When false, unreplaced variables are left as plain `{Variable name}`
   *  text instead of being wrapped in an HTML span. Defaults to true. */
  highlightUnreplaced?: boolean;
}

/**
 * Perform the variable replacement on a string.  Ideally, this shouldn't be consumed
 * directly, and should instead be returned from the usePresentationString hook.
 * @param inputString a markdown-formatted string, potentially with variables in it
 * @param formatterData household-aware data that will replace variables, if present
 * @returns a markdown string, with its variables replaced
 */
export function presentationStringFormatter({
  inputString,
  queryData,
  highlightUnreplaced = true,
}: presentationStringFormatterInput): string {
  return inputString.replace(/\{([^{}]+)\}/g, (match, variableName: string) => {
    const variable =
      PRESENTATION_STRINGS[variableName as keyof typeof PRESENTATION_STRINGS];

    if (!variable) {
      return highlightUnreplaced ? wrapUnreplacedVariable(match) : match;
    }

    const result = variable.formatter(queryData);

    // If the formatter returns null, leave the original token (with braces) in place
    if (result === null) {
      return highlightUnreplaced ? wrapUnreplacedVariable(match) : match;
    }

    // Escape resolved values when producing HTML output (highlightUnreplaced
    // mode), since values like names are user-controlled and rendered via
    // ContentEditable.
    return highlightUnreplaced ? escape(result) : result;
  });
}

function wrapUnreplacedVariable(match: string): string {
  return `<span style="background-color: ${COLORS.BLUE[100]}; border-radius: 4px; padding: 0 2px">${escape(match)}</span>`;
}
